import { hexToRgba } from '../utils';

/**
 * SectionDecorations — renders decorative background overlays
 * Usage: <SectionDecorations type={config.decoration} accentColor={s.accent} />
 * Place inside the section, positioned absolute.
 */
export default function SectionDecorations({ type, accentColor = '#6366f1', secondaryColor, dark = false }) {
    if (!type) return null;

    const c = accentColor;
    const c2 = secondaryColor || accentColor;

    // ── Grain / Noise texture ──
    if (type === 'grain') {
        return (
            <div className="absolute inset-0 pointer-events-none z-[1] opacity-[0.035]"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: '256px 256px',
                }}
            />
        );
    }

    // ── Blur spots / gradient blobs ──
    if (type === 'blur-spots') {
        return (
            <div className="absolute inset-0 pointer-events-none z-[1] overflow-hidden">
                <div className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 rounded-full blur-3xl opacity-[0.07] animate-[floatBlob_15s_ease-in-out_infinite]"
                    style={{ backgroundColor: c }} />
                <div className="absolute -bottom-1/4 -right-1/4 w-2/5 h-2/5 rounded-full blur-3xl opacity-[0.05] animate-[floatBlob_20s_ease-in-out_infinite_reverse]"
                    style={{ backgroundColor: c2 }} />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/3 h-1/3 rounded-full blur-3xl opacity-[0.04]"
                    style={{ backgroundColor: c }} />
                <style>{`@keyframes floatBlob{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(30px,-20px) scale(1.05)}66%{transform:translate(-20px,15px) scale(0.95)}}`}</style>
            </div>
        );
    }

    // ── Grid dots ──
    if (type === 'grid-dots') {
        const dotColor = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
        return (
            <div className="absolute inset-0 pointer-events-none z-[1]"
                style={{
                    backgroundImage: `radial-gradient(circle, ${dotColor} 1.5px, transparent 1.5px)`,
                    backgroundSize: '24px 24px',
                }}
            />
        );
    }

    // ── Gradient orbs ──
    if (type === 'gradient-orbs') {
        return (
            <div className="absolute inset-0 pointer-events-none z-[1] overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-[0.08] blur-3xl"
                    style={{ background: `radial-gradient(circle, ${c}, transparent 70%)` }} />
                <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-[0.06] blur-3xl"
                    style={{ background: `radial-gradient(circle, ${c2}, transparent 70%)` }} />
            </div>
        );
    }

    // ── Geometric shapes ──
    if (type === 'geometric') {
        const shapeColor = dark ? 'rgba(255,255,255,0.03)' : hexToRgba(c, 0.04);
        return (
            <div className="absolute inset-0 pointer-events-none z-[1] overflow-hidden">
                {/* Rotated square top-right */}
                <div className="absolute -top-12 -right-12 w-48 h-48 border-2 rotate-12 rounded-3xl"
                    style={{ borderColor: shapeColor }} />
                {/* Circle bottom-left */}
                <div className="absolute -bottom-8 -left-8 w-36 h-36 border-2 rounded-full"
                    style={{ borderColor: shapeColor }} />
                {/* Small diamond center-right */}
                <div className="absolute top-1/3 right-[8%] w-16 h-16 border rotate-45 rounded-lg"
                    style={{ borderColor: shapeColor }} />
                {/* Triangle-ish top-left */}
                <div className="absolute top-16 left-[12%] w-20 h-20 border-2 rounded-xl rotate-[30deg]"
                    style={{ borderColor: shapeColor }} />
            </div>
        );
    }

    // ── Decorative circles ──
    if (type === 'circles') {
        const ringColor = dark ? 'rgba(255,255,255,0.04)' : hexToRgba(c, 0.05);
        return (
            <div className="absolute inset-0 pointer-events-none z-[1] overflow-hidden">
                <div className="absolute -top-20 -right-20 w-64 h-64 border rounded-full" style={{ borderColor: ringColor }} />
                <div className="absolute -top-10 -right-10 w-44 h-44 border rounded-full" style={{ borderColor: ringColor }} />
                <div className="absolute -bottom-16 -left-16 w-52 h-52 border rounded-full" style={{ borderColor: ringColor }} />
                <div className="absolute -bottom-8 -left-8 w-32 h-32 border rounded-full" style={{ borderColor: ringColor }} />
            </div>
        );
    }

    // ── Floating cards ──
    if (type === 'floating-cards') {
        const cards = [
            { top: '10%', left: '5%', w: 72, h: 48, rotate: -12, delay: '0s' },
            { top: '60%', right: '4%', w: 64, h: 40, rotate: 8, delay: '2s' },
            { bottom: '15%', left: '8%', w: 56, h: 36, rotate: 6, delay: '4s' },
            { top: '25%', right: '10%', w: 48, h: 32, rotate: -6, delay: '6s' },
        ];
        return (
            <div className="absolute inset-0 pointer-events-none z-[1] overflow-hidden hidden md:block">
                {cards.map((card, i) => (
                    <div key={i} className="absolute rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 shadow-lg"
                        style={{
                            width: card.w, height: card.h,
                            top: card.top, left: card.left, right: card.right, bottom: card.bottom,
                            animation: `lmFloatCard${i} 8s ease-in-out ${card.delay} infinite`,
                        }} />
                ))}
                <style>{cards.map((card, i) =>
                    `@keyframes lmFloatCard${i}{0%,100%{transform:translateY(0) rotate(${card.rotate}deg)}50%{transform:translateY(-15px) rotate(${card.rotate}deg)}}`
                ).join('')}</style>
            </div>
        );
    }

    // ── Particles (CSS-only animated dots) ──
    if (type === 'particles') {
        const dots = [
            { s: 4, t: '12%', l: '8%',  d: '0s',   r: '6s'   },
            { s: 3, t: '35%', l: '22%', d: '1.2s', r: '8s'   },
            { s: 5, t: '60%', l: '5%',  d: '2.4s', r: '7s'   },
            { s: 3, t: '80%', l: '30%', d: '0.6s', r: '9s'   },
            { s: 4, t: '20%', l: '45%', d: '3s',   r: '6.5s' },
            { s: 6, t: '70%', l: '55%', d: '1.8s', r: '7.5s' },
            { s: 3, t: '45%', l: '70%', d: '0.3s', r: '8.5s' },
            { s: 5, t: '10%', l: '80%', d: '2.1s', r: '6s'   },
            { s: 4, t: '88%', l: '75%', d: '3.6s', r: '7s'   },
            { s: 3, t: '55%', l: '90%', d: '1.5s', r: '9s'   },
            { s: 6, t: '30%', l: '60%', d: '4.2s', r: '6.5s' },
            { s: 4, t: '75%', l: '42%', d: '2.7s', r: '8s'   },
            { s: 3, t: '5%',  l: '55%', d: '0.9s', r: '7.5s' },
            { s: 5, t: '92%', l: '18%', d: '3.3s', r: '6s'   },
            { s: 3, t: '48%', l: '38%', d: '1s',   r: '9.5s' },
            { s: 4, t: '22%', l: '95%', d: '4.8s', r: '7s'   },
        ];
        return (
            <div className="absolute inset-0 pointer-events-none z-[1] overflow-hidden">
                {dots.map((p, i) => (
                    <div key={i} className="absolute rounded-full" style={{
                        width: p.s, height: p.s, top: p.t, left: p.l,
                        backgroundColor: i % 3 === 0 ? c2 : c,
                        opacity: 0.25,
                        animation: `lmParticleDrift ${p.r} ease-in-out ${p.d} infinite`,
                    }} />
                ))}
                <style>{`@keyframes lmParticleDrift{0%,100%{transform:translate(0,0);opacity:.25}25%{transform:translate(8px,-12px);opacity:.45}50%{transform:translate(-6px,8px);opacity:.15}75%{transform:translate(10px,4px);opacity:.35}}`}</style>
            </div>
        );
    }

    return null;
}
